import { FC } from "react";
import propTypes from "prop-types";
import { TextField } from "@mui/material";

import { useDate, isValidDate } from "../../../../utility/useDate";
import { useReleaseManagement, ReleaseFlag } from "../../../../utility/feature-management";
import { getTimezoneOffset } from "date-fns-tz";
import { usePreferences } from "../../../../utility/usePreferences";

interface IDateFieldProps {
    label: string,
    value: string | null;
    onChange: (value: string) => void;
    minDate?: Date;
    maxDate?: Date;
    testId?: string;
    readOnly?: boolean;
    onClear?: () => void;
    onValidityChange?: (valid: boolean) => void;
}

const DateField: FC<IDateFieldProps> = (props) => {
    const isTimezoneOffsetReleased = useReleaseManagement(ReleaseFlag.releaseTimeZoneOffset);

    const { localizeDate } = useDate();
    const { timeZone } = usePreferences();

    const value = props.value ? new Date(props.value) : '';
    const localized = localizeDate(value);

    const minDate = props.minDate ? `${localizeDate(props.minDate).year}-${localizeDate(props.minDate).month.toString().padStart(2, "0")}-${localizeDate(props.minDate).day.toString().padStart(2, "0")}` : "";
    const maxDate = props.maxDate ? `${localizeDate(props.maxDate).year}-${localizeDate(props.maxDate).month.toString().padStart(2, "0")}-${localizeDate(props.maxDate).day.toString().padStart(2, "0")}` : "";
    let valueString = '';

    if (localized.isValid) {
        valueString = `${localized.year}-${localized.month.toString().padStart(2, "0")}-${localized.day.toString().padStart(2, "0")}`;
    }

    return (
        <TextField
            type="date"
            label={props.label}
            size="small"
            variant="outlined"
            disabled={props.readOnly == true}
            style={{ textTransform: "none", width: "100%", maxWidth: 223 }}
            InputLabelProps={{
                shrink: true,
            }}
            inputProps={{
                'data-testid': props.testId,
                'min': minDate,
                'max': maxDate
            }}
            value={valueString}
            onChange={(e) => {
                const input = e.currentTarget;
                props.onValidityChange?.(input.validity.valid);
                if (!input.validity.valid) {
                    return;
                }
                if (!input.value) {
                    props.onClear?.();
                    return;
                }
                let localizedSelectedDate;
                if (isTimezoneOffsetReleased) {
                    //example - if 28-02-23 is selected, e.currentValue.target equals 28-02-23 as a string (very important as time and timezone are NOT stored)

                    // stored as 28-02-23 00:00:00 UTC-8:00 which then becomes 27-02-23 16:00:00 UTC (America/Vancouver UTC-8:00)
                    // stored as 28-02-23 00:00:00 UTC+13:00 which then becomes 28-02-23 13:00:00 UTC (Pacific/Auckland UTC+13:00)
                    const selectedDate = new Date(e.currentTarget.value);

                    // get the timezone offset in minutes and convert to milliseconds using * 60000
                    // for -8:00, returns 480 minutes (8 hours difference) positive for behind utc
                    // for +13:00, returns -780 minutes (13 hours difference) negative for ahead of utc
                    const userTimezoneOffset = getTimezoneOffset(timeZone, selectedDate);

                    // remove offset on date to restore back to 28-02-23 00:00:00
                    // for 27-02-23 16:00:00 UTC (America/Vancouver UTC-8:00), it would be + 8hours
                    // for 28-02-23 13:00:00 UTC (Pacific/Auckland UTC+13:00), it would be + -13hours
                    const offsetSelectedDate = new Date(selectedDate.getTime() - userTimezoneOffset);

                    localizedSelectedDate = localizeDate(offsetSelectedDate);
                }
                else {
                    {
                        const selectedDate = new Date(e.currentTarget.value);
                        localizedSelectedDate = localizeDate(selectedDate);
                    }
                }

                if (!localizedSelectedDate.isValid) {
                    return;
                }

                const currentValue = localizeDate(
                    isValidDate(value)
                        ? value
                        : new Date(new Date().setSeconds(0, 0))
                );

                const withNewValues = currentValue.set({
                    year: localizedSelectedDate.year,
                    month: localizedSelectedDate.month,
                    day: localizedSelectedDate.day,
                });

                const isoString = withNewValues.toISO();
                if (isoString) {
                    props.onChange(isoString);
                }
            }}
        />
    );
}

DateField.propTypes = {
    label: propTypes.string.isRequired,
    value: propTypes.any,
    onChange: propTypes.func.isRequired
}

export default DateField;
